import { sha256 } from 'js-sha256';
import { JWTPayloadGrants, SessionToken } from '@/types';
import { LoggerService } from '@/utils';
import axios, { Axios } from 'axios';
import { CookieService } from './CookieService';
import { EnvironmentService } from './';
import { LockError } from '@/utils/LockError';
import { Base64 } from './Base64';

/**
 * Facilitates working with tokens (uses indexDB)
 */
export class TokenService {
  private readonly logger: LoggerService;
  private readonly basePath: string;
  private readonly fetch: Axios;
  private readonly cookieService: CookieService;
  // private readonly environmentService: EnvironmentService;

  constructor() {
    this.logger = new LoggerService(this.constructor.name);
    this.cookieService = new CookieService();
    this.basePath = 'v3/tokens';
    this.fetch = axios.create({ baseURL: EnvironmentService.getAPIHost() });
    this.logger.log('init()');
  }

  /**
   * Tokens returned from this are not guaranteed to be valid so be sure to do required
   * validation if it is required.
   *
   * @param token
   * @returns
   */
  public static payload(token: string): { [key: string]: string } {
    try {
      const payload: string = JSON.parse(Base64.decode(token.split('.')[1]));
      return payload as any;
    } catch (error) {
      console.error('TokenService payload(): Failed to decode');
      throw error;
    }
  }

  /**
   * Set what should happen when the token expires
   * @param token to use when determining the expiration.
   * @param toExecute when the the token expires.
   * @return the timeout identifier.
   */
  public onExpiration(token: string, toExecute: () => void): number {
    let unverifiedPayload: any;
    try {
      unverifiedPayload = TokenService.payload(token);
    } catch (error) {
      this.logger.error('onExpiration() token malformed', error);
      throw new LockError('token-decode-failed', (error as any).message);
    }

    const expiration: number = unverifiedPayload.exp;
    const nowTime: number = Date.now() / 1000;

    if (expiration < nowTime) {
      this.logger.debug('onExpiration() SessionToken has expired.');
      throw new LockError('token-expired', 'The token provided has expired.');
    }

    return window.setTimeout(() => toExecute(), Math.round(expiration * 1000));
  }

  /**
   *
   * @param token to use when determining the expiration of the interval.
   * @param interval in ms to wait between each execution.
   * @param toExecute on each interval.
   * @return first the interval Id, second the timeout id that will clear the interval on token expiration.
   */
  public untilExpired(
    token: string,
    interval: number,
    toExecute: () => void
  ): number[] {
    let unverifiedPayload: any;
    try {
      unverifiedPayload = TokenService.payload(token);
    } catch (error) {
      this.logger.error('untilExpired() ', error);
      throw error;
    }

    const expiration: number = unverifiedPayload.exp;
    const nowTime: number = Date.now() / 1000;

    if (expiration < nowTime) {
      this.logger.debug('validate() SessionToken has expired.');
      throw new LockError('token-expired', 'The token provided has expired.');
    }

    const intervalId: number = window.setInterval(() => toExecute(), interval);
    const timeoutId: number = this.onExpiration(token, () =>
      window.clearInterval(intervalId)
    );

    return [intervalId, timeoutId];
  }

  /**
   * Tokens are stored according to the application ID so we can ensure data separation
   * appropriate to the correct authentication a user has been granted.
   *
   * @param applicationId to get the token for.
   */
  public getApplicationToken(applicationId: string): string | undefined {
    this.logger.debug('getApplicationToken()', { applicationId });
    const token: string | null = localStorage.getItem(`${applicationId}_token`);
    this.logger.debug('getApplicationToken()', { token });
    return token || undefined;
  }

  /**
   * Validate token
   * @param token to execute static validation on.
   * @param payload grants to validation where each grant will be required to have the value present.
   *In the case of scope, then this sets the minimal list of scopes, additional scopes
   *if present on the token, will be ignored.
   *
   */
  public validate(
    token: string | SessionToken,
    payloadRequirements?: { [key in JWTPayloadGrants]?: string | number } // eslint-disable-line
  ): boolean {
    this.logger.debug('validate() input', { token });

    if (!token) {
      throw new LockError(
        'token-not-provided',
        'No token was found or provided.'
      );
    }

    const tokenString = typeof token === 'string' ? token : token.token;

    this.logger.debug('validate() convert to string.', { tokenString });

    if (!tokenString) {
      throw new LockError(
        'token-not-provided',
        'No token was found or provided.'
      );
    }
    const parts: string[] = tokenString.split('.');

    this.logger.debug('validate() parts of the token', { parts });

    if (parts.length !== 3) {
      throw new LockError(
        'token-malformed',
        'The token is not valid, it should be broken into 3 parts (separated by dots).'
      );
    }

    let unverifiedPayload: any;
    try {
      unverifiedPayload = TokenService.payload(tokenString);
    } catch (error) {
      this.logger.error('validate() token malformed', error);
      throw new LockError('token-decode-failed', (error as any).message);
    }

    const expiration: number = unverifiedPayload.exp;
    const nowTime: number = Date.now() / 1000;

    this.logger.debug('validate() token contents and data for validation.', {
      unverifiedPayload,
      expiration,
      nowTime,
    });

    if (expiration < nowTime) {
      this.logger.debug('validate() SessionToken has expired.');
      throw new LockError('token-expired', 'The token provided has expired.');
    }

    /**
     * Scope is always required, so do not bother with the comparison on the body if it is omitted.
     */
    if (!unverifiedPayload.scope) {
      this.logger.debug('validate() SessionToken no scope.');
      throw new LockError(
        'token-scope-missing',
        'The token does not have any scope defined.'
      );
    }

    this.checkGrant('scope', 'scope', unverifiedPayload, payloadRequirements);
    this.checkGrant('aud', 'aud', unverifiedPayload, payloadRequirements);
    this.checkGrant('sub', 'sub', unverifiedPayload, payloadRequirements);
    this.checkGrant(
      'role',
      'https://authvia.com/role',
      unverifiedPayload,
      payloadRequirements
    );
    this.checkGrant(
      'conversation',
      'https://authvia.com/conversation',
      unverifiedPayload,
      payloadRequirements
    );
    this.checkGrant(
      'merchant',
      'https://authvia.com/merchant',
      unverifiedPayload,
      payloadRequirements
    );
    this.checkGrant(
      'partner',
      'https://authvia.com/partner',
      unverifiedPayload,
      payloadRequirements
    );
    this.checkGrant(
      'user',
      'https://authvia.com/user',
      unverifiedPayload,
      payloadRequirements
    );
    this.checkGrant(
      'uuid',
      'https://authvia.com/uuid',
      unverifiedPayload,
      payloadRequirements
    );

    return true;
  }

  /**
   * When a token is successfully verified, it will be stored in the local storage
   * to be recalled later by getApplicationToken()
   *
   * Removes stored token only when expired/can be decoded correctly
   * Use removeApplicationToken() to remove tokens manually
   * @param token to verify
   * @returns
   */
  public async verify(
    token: string
  ): Promise<{ [key: string]: string } | undefined> {
    let payload: { [key: string]: string };
    let jti: string;
    let azp: string;

    try {
      payload = TokenService.payload(token);
      jti = payload.jti;
      azp = payload.azp;
    } catch (error) {
      this.logger.error('verify() token malformed', error);
      throw new LockError('token-decode-failed', (error as any).message);
    }

    try {
      this.logger.debug('verify() request', { jti, payload });

      const verifiedResponse: {
        verified: boolean;
        payload: { [key in JWTPayloadGrants]: string }; // eslint-disable-line
      } = await this.fetch.post(this.basePath + '/' + jti, {
        type: 'Bearer',
        token,
      });
      console.log('verifiedResponse', verifiedResponse.verified);
      this.logger.debug('verify() response', { verifiedResponse });

      localStorage.setItem(`${azp}_token`, token);

      this.logger.debug('verify() token valid and stored.', {
        payload: verifiedResponse.payload,
      });

      return verifiedResponse.payload;
    } catch (error) {
      this.logger.error('verify() token malformed', error);
      localStorage.removeItem(azp);
      throw new LockError('token-verify-failed', (error as any).message);
    }
  }

  /**
   * Remove the application token stored
   * @param applicationId Application ID
   */
  public async removeApplicationToken(applicationId: string) {
    try {
      localStorage.removeItem(applicationId);
    } catch (error) {
      this.logger.error(
        'removeApplicationToken() Failed to remove token',
        error
      );
      throw new LockError(
        'token-verify-failed',
        'Failed to remove application token'
      );
    }
  }

  /**
   * Check token grant
   * @param name to present in the error message that is a friendly description of the grant.
   * @param grantName to check on the token.
   * @param tokenPayload to validate.
   * @param requirements to check on the token.
   * @returns
   */
  private checkGrant(
    name: string,
    grantName: JWTPayloadGrants,
    tokenPayload: any,
    requirements?: { [key in JWTPayloadGrants]?: string | number } // eslint-disable-line
  ) {
    if (!requirements) return;

    this.logger.debug('checkGrant() checking grant for', { grantName, name });

    switch (grantName) {
      case 'aud':
        /*
         * Audience is tricky because it can be a string or an array of strings on the target token.
         */
        if (
          (Array.isArray(requirements.aud) &&
            !requirements.aud.includes(requirements.aud)) ||
          requirements.aud !== requirements.aud
        ) {
          throw new LockError(
            'token-no-aud-access',
            `The tokens ${name} does not match the required audience, ${tokenPayload.aud} present but ${requirements.aud} expected.`
          );
        }
        break;
      case 'scope': {
        // If the requirements didn't define scope don't check for them
        if (requirements.scope == null || requirements.scope == undefined) {
          break;
        }

        const tokenScopes: string[] = tokenPayload.scope.split(' ');

        const missingScope: boolean = (requirements.scope as string)
          .split(' ')
          .reduce((isMissing: boolean, scope: string) => {
            if (isMissing) {
              return isMissing;
            }
            return !tokenScopes.includes(scope);
          }, false);

        if (missingScope) {
          throw new LockError(
            'token-scope-missing',
            'The token does not have all of the scopes required.'
          );
        }
        break;
      }

      default:
        if (
          requirements[grantName] &&
          requirements[grantName] !== requirements[grantName]
        ) {
          throw new LockError(
            `token-no-${name}-access`,
            `The tokens ${name} does not match the required audience, ${tokenPayload[grantName]} present but ${requirements[grantName]} expected.`
          );
        }
        break;
    }
  }

  /**
   * Method to register PKCE (Public Key Code Exchange) token challenge.
   *
   * @param clientId Application ID aka AZP
   * @returns Promise<any>
   */
  public async registerPKCEToken(
    clientId: string,
    token?: string
  ): Promise<any> {
    this.logger.debug('registerPKCEToken() ', clientId);
    // Clear the verifier for each PKCE exchange
    this.deleteVerifier();

    const deviceId: string = this.cookieService.getDeviceId();
    const verifier: string = this.getVerifier();
    const authToken: string | undefined | null =
      token || this.getApplicationToken(clientId);

    const challenge = sha256(verifier);

    const requestData = {
      client_id: clientId,
      device_id: deviceId,
      challenge: challenge,
    };
    return this.fetch.post(`${this.basePath}/pkces`, requestData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
  }

  /**
   * Method to get payment_methods:pci scope on a token by PKCE exchange
   *
   * @param clientId Application ID aka AZP
   * @returns Promise
   */
  public async getPaymentMethodsPCIToken(
    clientId: string,
    token?: string
  ): Promise<SessionToken> {
    this.logger.debug('getPaymentMethodPCIToken() ', clientId);
    const deviceId: string = this.cookieService.getDeviceId();
    const verifier: string = await this.getVerifier();
    const authToken: string | undefined | null =
      token || this.getApplicationToken(clientId);

    const requestData = {
      token: authToken,
      device_id: deviceId,
      verifier: verifier,
    };

    return await this.fetch.post(this.basePath, requestData);
  }

  /**
   * Method to get the verifier
   */
  private getVerifier(): string {
    let verifier: string | null = localStorage.getItem('av-verifier');

    if (!verifier) {
      verifier = crypto.randomUUID();
      try {
        localStorage.setItem('av-verifier', verifier!);
      } catch (error: unknown) {
        this.logger.error('getVerifier() failed ', (error as any).message);
        throw error;
      }
    }

    return verifier as string;
  }

  private deleteVerifier() {
    try {
      localStorage.removeItem('av-verifier');
    } catch (error: unknown) {
      this.logger.error('deleteVerifier() failed ', (error as any).message);
      throw error;
    }
  }

  /**
   * Method to generate authorization code.
   *
   * @param mobileNumber mobile number
   * @param clientId Application ID
   */
  public async generateCode(
    mobileNumber: string,
    clientId: string,
    scope?: string,
    expiration?: string
  ): Promise<any> {
    const deviceId: string = this.cookieService.getDeviceId();
    const verifier: string = this.getVerifier();
    const reqData: any = {
      client_id: clientId,
      device_id: deviceId,
      address: mobileNumber,
      verifier: verifier,
    };

    if (scope) reqData['scope'] = scope;
    if (expiration) reqData['expiration'] = expiration;

    return await this.fetch.post(this.basePath, reqData);
  }

  /**
   * Method to submit authorization code.
   *
   * @param authCode auth code
   * @param clientId Application ID
   */
  public async submitCode(authCode: number, clientId: string): Promise<any> {
    const deviceId = this.cookieService.getDeviceId();
    const reqData = {
      client_id: clientId,
      device_id: deviceId,
      code: authCode,
    };

    return this.fetch.post(this.basePath, reqData);
  }

  /**
   * Method to get address.
   *
   * @param address email/phone
   */
  public async getAddress(address: string): Promise<any> {
    return this.fetch.get(`${address}`);
  }
}
